<template>
    <div>
    <v-navigation-drawer 
        v-model="drawerModel" 
        fixed 
        right 
        temporary 
        :persistent="isSidebarCloseLocked"
        :width="drawerWidth"
        class="connector-settings-sidebar"
        @input="handleDrawerInput"
    >
        <div class="sidebar-content">
            <AddChannelSidebarContent 
                v-if="channelCreationMode"
                @close="closeSideBar"
            />
            <ChangeChannelTypeSidebarContent
                v-else-if="isChangeChannelMode"
                :channel-display-name="changeChannelSourceChannelDisplayName"
                :channel-phone="changeChannelSourceChannelPhone"
                @back="goBackFromChangeChannel"
                @continue="continueChangeChannelFlow"
            />
            <ConnectorSettingsContent 
                v-else-if="settingsConnectorId"
                :key="settingsConnectorId"
                :connectorId="settingsConnectorId"
                @close="closeSideBar"
            />
        </div>
    </v-navigation-drawer>
    <CancelSubscriptionSidebarContent
        :opened="isCancelSubscriptionMode"
        :is-tariff-cancel-mode="isTariffCancelSubscriptionMode"
        :is-module-cancel-mode="isModuleCancelSubscriptionMode"
        :with-tariff-renewal="cancelSubscriptionWithTariff"
        :channel-display-name="cancelSubscriptionChannelDisplayName"
        :period-to="cancelSubscriptionPeriodTo"
        :loading="cancelSubscriptionInProgress"
        @back="goBackFromCancelSubscription"
        @confirm="confirmCancelSubscription"
    />
    </div>
</template>

<script>
import ConnectorSettingsContent from './ConnectorSettingsSidebarContent.vue'
import AddChannelSidebarContent from './AddChannelSidebarContent.vue'
import ChangeChannelTypeSidebarContent from './ChangeChannelTypeSidebarContent.vue'
import CancelSubscriptionSidebarContent from './CancelSubscriptionSidebarContent.vue'
import {
    getChannelBaseNameByProviderType,
    getChannelGroupByProviderType,
} from '@/helpers/channels/channelCatalog'
import { toggleSubscription } from '@/api/marketplace'
import { getSlots, unchainSlot } from '@/api/networkFixer'
import { useBillingStore } from '@/store/stores/billing'
import { getConnectorChannelDisplayName } from '@/helpers/channels/getConnectorChannelDisplayName'

export default {
    name: 'ConnectorSettingsSidebar',
    components: {
        ConnectorSettingsContent,
        AddChannelSidebarContent,
        ChangeChannelTypeSidebarContent,
        CancelSubscriptionSidebarContent,
    },
    data: () => ({
        // Локальная модель для v-model drawer.
        // Нужна, чтобы корректно "откатывать" закрытие (клик вне/ESC),
        // даже если store-значение не меняется (иначе drawer может закрыться визуально и "залипнуть").
        drawerModel: false,
        cancelSubscriptionInProgress: false,
        cancelSubscriptionResolvedChannelName: '',
        cancelSubscriptionResolvedRenewalMode: null,
        cancelSubscriptionResolvedPeriodTo: '',
        isNestedChannelModalOpened: false,
    }),
    computed: {
        // На узких экранах (< 490px) сайдбар занимает всю ширину viewport,
        // иначе вылезает за край из-за фиксированной ширины.
        drawerWidth() {
            const viewportWidth = this.$vuetify?.breakpoint?.width
            if (typeof viewportWidth === 'number' && viewportWidth > 0 && viewportWidth < 490) {
                return viewportWidth
            }
            return 490
        },
        isConnectorSettingsSidebar() {
            return this.$store.state.isConnectorSettingsSidebar
        },
        connectorSettingsSidebarId() {
            return this.$store.state.connectorSettingsSidebarId
        },
        channelCreationMode() {
            return this.$store.state.channelCreationMode
        },
        isChangeChannelMode() {
            return typeof this.connectorSettingsSidebarId === 'string' && this.connectorSettingsSidebarId.startsWith('change-channel')
        },
        isCancelSubscriptionMode() {
            return typeof this.connectorSettingsSidebarId === 'string' && this.connectorSettingsSidebarId.startsWith('cancel-subscription')
        },
        isTariffCancelSubscriptionMode() {
            if (!this.isCancelSubscriptionMode || typeof this.connectorSettingsSidebarId !== 'string') return false
            const parts = this.connectorSettingsSidebarId.split(':')
            return parts[1] === 'tariff'
        },
        isModuleCancelSubscriptionMode() {
            if (!this.isCancelSubscriptionMode || typeof this.connectorSettingsSidebarId !== 'string') return false
            const parts = this.connectorSettingsSidebarId.split(':')
            return parts[1] === 'module'
        },
        isDirectSubscriptionCancelMode() {
            return this.isTariffCancelSubscriptionMode || this.isModuleCancelSubscriptionMode
        },
        cancelSubscriptionTariffId() {
            if (!this.isDirectSubscriptionCancelMode || typeof this.connectorSettingsSidebarId !== 'string') return null
            const parts = this.connectorSettingsSidebarId.split(':')
            return parts[2] || null
        },
        changeChannelSourceConnectorId() {
            if (!this.isChangeChannelMode || typeof this.connectorSettingsSidebarId !== 'string') return null
            const [, connectorId] = this.connectorSettingsSidebarId.split(':')
            return connectorId || null
        },
        changeChannelSourceSlotId() {
            if (!this.isChangeChannelMode || typeof this.connectorSettingsSidebarId !== 'string') return null
            const parts = this.connectorSettingsSidebarId.split(':')
            return parts[2] || null
        },
        currentProject() {
            const projectId = this.$route?.params?.id
            return projectId ? this.$store.getters.projectById(projectId) : null
        },
        changeChannelSourceConnector() {
            const connectorId = this.changeChannelSourceConnectorId
            if (!connectorId) return null
            const connectors = this.currentProject?.connectors || []
            return connectors.find(c => (c.connectorId || c.id) === connectorId) || null
        },
        changeChannelSourceChannelDisplayName() {
            const connector = this.changeChannelSourceConnector
            if (!connector) return ''
            const baseName = this.getChannelBaseName(connector)
            const index = this.getChannelIndex(connector)
            return index <= 1 ? baseName : `${baseName} ${index}`
        },
        changeChannelSourceChannelPhone() {
            return this.changeChannelSourceConnector?.phone || ''
        },
        cancelSubscriptionConnectorId() {
            if (!this.isCancelSubscriptionMode || typeof this.connectorSettingsSidebarId !== 'string') return null
            const parts = this.connectorSettingsSidebarId.split(':')
            if (parts[1] === 'slot' || parts[1] === 'tariff' || parts[1] === 'module') return null
            return parts[1] || null
        },
        cancelSubscriptionSlotId() {
            if (!this.isCancelSubscriptionMode || typeof this.connectorSettingsSidebarId !== 'string') return null
            const parts = this.connectorSettingsSidebarId.split(':')
            if (parts[1] !== 'slot') return null
            return parts[2] || null
        },
        cancelSubscriptionWithTariff() {
            return (
                !this.isDirectSubscriptionCancelMode &&
                this.cancelSubscriptionResolvedRenewalMode === 'with_tariff'
            )
        },
        cancelSubscriptionChannelDisplayName() {
            const fromQuery = String(this.cancelSubscriptionChannelNameFromQuery || '').trim()
            if (fromQuery) return fromQuery
            if (this.cancelSubscriptionResolvedChannelName) return this.cancelSubscriptionResolvedChannelName
            return this.changeChannelSourceChannelDisplayName || ''
        },
        cancelSubscriptionProjectId() {
            return this.$route?.params?.id || this.currentProject?.id || ''
        },
        isStandaloneCancelSubscriptionMode() {
            if (!this.isCancelSubscriptionMode || typeof this.connectorSettingsSidebarId !== 'string') return false
            const parts = this.connectorSettingsSidebarId.split(':')
            return parts[parts.length - 1] === 'from-overview'
        },
        isCancelModalOnly() {
            return (
                this.isDirectSubscriptionCancelMode ||
                this.isStandaloneCancelSubscriptionMode ||
                (this.isCancelSubscriptionMode &&
                    Boolean(this.cancelSubscriptionSlotId) &&
                    !this.cancelSubscriptionConnectorId)
            )
        },
        isCancelOverChannelSettings() {
            return (
                this.isCancelSubscriptionMode &&
                Boolean(this.cancelSubscriptionConnectorId) &&
                !this.isStandaloneCancelSubscriptionMode &&
                !this.isDirectSubscriptionCancelMode
            )
        },
        settingsConnectorId() {
            if (this.channelCreationMode || this.isChangeChannelMode) return null
            if (this.isCancelOverChannelSettings) return this.cancelSubscriptionConnectorId
            if (this.isCancelSubscriptionMode) return null
            return this.connectorSettingsSidebarId
        },
        cancelSubscriptionChannelNameFromQuery() {
            return String(this.cancelSubscriptionResolvedChannelName || '').trim()
        },
        cancelSubscriptionPeriodTo() {
            return String(this.cancelSubscriptionResolvedPeriodTo || '').trim()
        },
        isChannelCreationLocked() {
            // Блокируем закрытие/делаем persistent только в режиме создания канала
            return !!this.$store.state.channelCreationMode && (this.$store.state.channelCreationLockCount || 0) > 0
        },
        isSidebarCloseLocked() {
            return this.isChannelCreationLocked || this.isNestedChannelModalOpened
        },
    },
    watch: {
        connectorSettingsSidebarId: {
            handler() {
                void this.resolveCancelSubscriptionContext()
                if (!this.isConnectorSettingsSidebar) return
                this.drawerModel = !this.isCancelModalOnly
            },
            immediate: true,
        },
        isConnectorSettingsSidebar: {
            immediate: true,
            handler(newValue) {
                if (!newValue) {
                    this.drawerModel = false
                    this.$store.commit('setConnectorSettingsSidebarId', null)
                    this.clearSidebarQueryParams()
                    return
                }
                if (this.isCancelModalOnly) {
                    this.drawerModel = false
                    return
                }
                this.drawerModel = true
            },
        },
        // Защита от рассинхронизации: Vuetify может закрыть drawer визуально,
        // даже если мы блокируем изменение store (например, при клике вне/ESC во время lock).
        // В таком случае drawerModel становится false, а store остаётся true — и кнопки "Добавить"
        // перестают "открывать" сайдбар (store не меняется, watcher не срабатывает).
        drawerModel(newValue) {
            const storeOpen = !!this.$store.state.isConnectorSettingsSidebar
            if (!newValue && storeOpen) {
                if (this.isCancelModalOnly) {
                    return
                }
                if (this.isSidebarCloseLocked) {
                    // Откатываем визуальное закрытие
                    this.drawerModel = true
                    this.$nextTick(() => {
                        this.drawerModel = true
                    })
                    setTimeout(() => {
                        this.drawerModel = true
                    }, 0)
                    setTimeout(() => {
                        this.drawerModel = true
                    }, 100)
                    return
                }

                // Если lock уже снят — считаем, что пользователь действительно закрыл drawer,
                // и закрываем корректно через общий reset.
                this.closeSideBar()
            }
        },
        // Отслеживаем изменения роута, но не закрываем сайдбар при изменении только query параметров
        '$route'(to, from) {
            // Если изменился только query параметр (не path), не закрываем сайдбар
            if (to.path === from.path && this.channelCreationMode) {
                return
            }
            // Если изменился path, закрываем сайдбар (closeSideBar сам проверит, открыт ли он)
            if (to.path !== from.path && !this.channelCreationMode) {
                this.closeSideBar()
            }
        },
    },
    mounted() {
        this.$root.$on('connector-settings-modal-state', this.setNestedChannelModalState)
    },
    beforeDestroy() {
        this.$root.$off('connector-settings-modal-state', this.setNestedChannelModalState)
    },
    methods: {
        setNestedChannelModalState(opened) {
            this.isNestedChannelModalOpened = !!opened
            if (opened && this.$store.state.isConnectorSettingsSidebar) {
                this.drawerModel = true
            }
        },
        async resolveCancelSubscriptionContext() {
            this.cancelSubscriptionResolvedChannelName = ''
            this.cancelSubscriptionResolvedRenewalMode = null
            this.cancelSubscriptionResolvedPeriodTo = ''

            if (!this.isCancelSubscriptionMode) return

            const searchParams =
                typeof window !== 'undefined' ? new URL(window.location.href).searchParams : null
            const queryName = String(searchParams?.get('cancelChannelName') || '').trim()
            const queryPeriodTo = String(searchParams?.get('cancelPeriodTo') || '').trim()
            if (queryName) this.cancelSubscriptionResolvedChannelName = queryName
            if (queryPeriodTo) this.cancelSubscriptionResolvedPeriodTo = queryPeriodTo

            const projectId = this.$route?.params?.id
            if (this.isDirectSubscriptionCancelMode) {
                if (!this.cancelSubscriptionResolvedPeriodTo && projectId && this.isTariffCancelSubscriptionMode) {
                    this.cancelSubscriptionResolvedPeriodTo = String(
                        useBillingStore().getTariffPeriodEnd(projectId) || ''
                    ).trim()
                }
                return
            }

            if (!projectId || (!this.cancelSubscriptionConnectorId && !this.cancelSubscriptionSlotId)) {
                return
            }

            const { data, error } = await getSlots({ projectId })
            if (error || !Array.isArray(data)) {
                return
            }

            const slot = this.cancelSubscriptionSlotId
                ? data.find((s) => String(s?.id) === String(this.cancelSubscriptionSlotId))
                : data.find(
                      (s) => String(s?.channelConnectorId) === String(this.cancelSubscriptionConnectorId)
                  )

            if (!slot) return

            this.cancelSubscriptionResolvedRenewalMode =
                slot.renewalMode === 'with_tariff' ? 'with_tariff' : 'standalone'

            if (!this.cancelSubscriptionResolvedPeriodTo) {
                this.cancelSubscriptionResolvedPeriodTo = String(slot.nextPayment || '').trim()
            }

            if (slot.channelConnectorId) {
                const connector = (this.currentProject?.connectors || []).find(
                    (c) => (c.connectorId || c.id) === slot.channelConnectorId
                )
                const fromConnector = getConnectorChannelDisplayName(
                    connector,
                    this.currentProject?.connectors || []
                )
                if (fromConnector) this.cancelSubscriptionResolvedChannelName = fromConnector
            }
        },
        getChannelGroupKey(connector) {
            const providerType = connector?.providerType
            const group = getChannelGroupByProviderType(providerType)
            if (group) return group
            return providerType || connector?.connectorType || 'other'
        },
        getChannelBaseName(connector) {
            const providerType = connector?.providerType
            return getChannelBaseNameByProviderType(providerType)
        },
        getChannelIndex(connector) {
            const connectors = (this.currentProject?.connectors || [])
                .filter(c => !c?.isChisai)
                .filter(c => this.getChannelGroupKey(c) === this.getChannelGroupKey(connector))
                .sort((a, b) => {
                    const aTime = a?.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b?.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return aTime - bTime
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                    return String(a?.connectorId || '').localeCompare(String(b?.connectorId || ''))
                })
            const idx = connectors.findIndex(c => (c?.connectorId || c?.id) === (connector?.connectorId || connector?.id))
            return idx >= 0 ? idx + 1 : 1
        },
        handleDrawerInput(value) {
            // Запрещаем закрывать сайдбар во время критичных этапов создания/сохранения приоритетов
            if (!value && this.isSidebarCloseLocked) {
                if (this.isChannelCreationLocked) {
                    this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                }
                // принудительно оставляем открытым (локально), даже если store уже true
                this.drawerModel = true
                return
            }

            if (!value) {
                if (this.isCancelModalOnly) {
                    this.drawerModel = false
                    return
                }
                this.closeSideBar()
                return
            }

            this.$store.commit('setIsConnectorSettingsSidebar', true)
        },
        closeSideBar() {
            if (!this.isConnectorSettingsSidebar && !this.drawerModel && !this.channelCreationMode) {
                return
            }
            if (this.isChannelCreationLocked) {
                this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                this.drawerModel = true
                return
            }
            this.$store.commit('setIsConnectorSettingsSidebar', false)
            this.$store.commit('setConnectorSettingsSidebarId', null)
            const wasStarterPackContext = this.$store.state.channelCreationContext === 'starter-pack'
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            this.$store.commit('setChannelCreationContext', null)
            this.$store.commit('resetChannelCreationLock')
            this.$store.commit('project/setLoggedAddingConnector', null)
            this.$store.commit('project/updateCurrentAddingConnector', null)
            this.clearSidebarQueryParams()
            if (wasStarterPackContext) {
                this.$root.$emit('reload-channel-slots')
                this.$root.$emit('starter-pack-cascade-reload')
            }
        },
        clearSidebarQueryParams() {
            // Очищаем query-параметры напрямую из URL:
            // часть параметров выставляется через history.replaceState и может
            // не попадать сразу в this.$route.query.
            const newUrl = new URL(window.location.href)
            const hadParams =
                newUrl.searchParams.has('group') ||
                newUrl.searchParams.has('slotId') ||
                newUrl.searchParams.has('connectTab') ||
                newUrl.searchParams.has('changeChannel') ||
                newUrl.searchParams.has('cancelPeriodTo') ||
                newUrl.searchParams.has('cancelTariffItems') ||
                newUrl.searchParams.has('cancelRenewalMode') ||
                newUrl.searchParams.has('cancelChannelName')
            if (!hadParams) return
            newUrl.searchParams.delete('group')
            newUrl.searchParams.delete('slotId')
            newUrl.searchParams.delete('connectTab')
            newUrl.searchParams.delete('changeChannel')
            newUrl.searchParams.delete('cancelPeriodTo')
            newUrl.searchParams.delete('cancelTariffItems')
            newUrl.searchParams.delete('cancelRenewalMode')
            newUrl.searchParams.delete('cancelChannelName')
            window.history.replaceState({}, '', newUrl.toString())
        },
        goBackFromChangeChannel() {
            if (!this.changeChannelSourceConnectorId) {
                this.closeSideBar()
                return
            }
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            this.$store.commit('setConnectorSettingsSidebarId', this.changeChannelSourceConnectorId)
            this.$store.commit('setIsConnectorSettingsSidebar', true)
        },
        goBackFromCancelSubscription() {
            if (this.isDirectSubscriptionCancelMode || this.isStandaloneCancelSubscriptionMode) {
                this.closeSideBar()
                return
            }
            if (!this.cancelSubscriptionConnectorId) {
                if (this.cancelSubscriptionSlotId) {
                    this.$store.commit('setConnectorSettingsSidebarId', null)
                    this.$store.commit('setChannelCreationMode', true)
                    this.$store.commit('setChannelCreationStage', 1)
                    this.$store.commit('setIsConnectorSettingsSidebar', true)
                    return
                }
                this.closeSideBar()
                return
            }
            this.$store.commit('setChannelCreationMode', false)
            this.$store.commit('setChannelCreationStage', null)
            this.$store.commit('setConnectorSettingsSidebarId', this.cancelSubscriptionConnectorId)
            this.$store.commit('setIsConnectorSettingsSidebar', true)
        },
        async confirmCancelSubscription(cancellationReason) {
            const projectId = this.$route?.params?.id
            const connectorId = this.cancelSubscriptionConnectorId
            const slotId = this.cancelSubscriptionSlotId
            const directSubscriptionId = Number(this.cancelSubscriptionTariffId)
            if (!projectId) {
                this.$store.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (this.isDirectSubscriptionCancelMode && !Number.isFinite(directSubscriptionId)) {
                this.$store.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }
            if (!this.isDirectSubscriptionCancelMode && !connectorId && !slotId) {
                this.$store.dispatch('callNotify', 'Не удалось определить канал для отмены подписки')
                return
            }
            this.cancelSubscriptionInProgress = true
            try {
                let subscriptionId = null
                let shouldDetachFromTariff = false
                if (this.isDirectSubscriptionCancelMode) {
                    subscriptionId = directSubscriptionId
                } else {
                    const { data, error } = await getSlots({ projectId })
                    if (error || !Array.isArray(data)) {
                        this.$store.dispatch('callNotify', 'Не удалось получить данные слотов')
                        return
                    }
                    const slot = slotId
                        ? data.find((s) => String(s?.id) === String(slotId))
                        : data.find((s) => String(s?.channelConnectorId) === String(connectorId))
                    subscriptionId = slot?.subscriptionId
                    shouldDetachFromTariff = slot?.renewalMode === 'with_tariff'
                    if (!subscriptionId) {
                        this.$store.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                        return
                    }
                }
                if (shouldDetachFromTariff) {
                    const { error: unchainError } = await unchainSlot({ subscriptionId })
                    if (unchainError) {
                        this.$store.dispatch('callNotify', 'Не удалось убрать канал из подписки')
                        return
                    }
                }
                const { error: toggleError } = await toggleSubscription({
                    projectId,
                    subscriptionId,
                    status: 'disable',
                    cancellationReason,
                })
                if (toggleError) {
                    this.$store.dispatch('callNotify', 'Не удалось отключить автопродление')
                    return
                }
                const billingStore = useBillingStore()
                await billingStore.fetchCurrentTariff(projectId)
                this.$root.$emit('reload-channel-slots')
                this.$root.$emit('reload-subscriptions')
                this.$root.$emit('reload-slot-billing-state')
                this.$store.dispatch('callNotify', 'Автопродление отключено')
                this.closeSideBar()
            } finally {
                this.cancelSubscriptionInProgress = false
            }
        },
        async continueChangeChannelFlow(selectedGroupId) {
            if (!this.changeChannelSourceConnectorId || !selectedGroupId) return
            try {
                await this.$store.dispatch('project/deleteConnector', this.changeChannelSourceConnectorId)
                this.$root.$emit('reload-channel-slots')
                this.$root.$emit('reload-project-promos')
            } catch (e) {
                this.$store.dispatch('callNotify', 'Не удалось удалить текущий канал')
                return
            }

            this.$store.commit('project/setLoggedAddingConnector', null)
            this.$store.commit('project/updateCurrentAddingConnector', null)
            // Для AddChannelSidebarContent сохраняем служебные параметры в URL
            // до переключения в режим создания, чтобы slotId гарантированно
            // подхватился при инициализации компонента.
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.set('group', selectedGroupId)
            newUrl.searchParams.set('connectTab', 'new')
            if (this.changeChannelSourceSlotId) newUrl.searchParams.set('slotId', this.changeChannelSourceSlotId)
            else newUrl.searchParams.delete('slotId')
            newUrl.searchParams.delete('changeChannel')
            window.history.replaceState({}, '', newUrl.toString())

            this.$store.commit('setConnectorSettingsSidebarId', null)
            this.$store.commit('setChannelCreationMode', true)
            this.$store.commit('setChannelCreationStage', 1)
            this.$store.commit('setIsConnectorSettingsSidebar', true)
            // Дополнительно фиксируем локальную модель drawer,
            // чтобы исключить визуальное закрытие из-за гонок состояния.
            this.drawerModel = true
            this.$nextTick(() => {
                this.drawerModel = true
            })
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.v-navigation-drawer
    will-change: initial
    z-index: 2000 !important
    overscroll-behavior: contain

::v-deep .connector-settings-sidebar
    min-width: 0 !important
    max-width: 100vw !important
    width: min(490px, 100vw) !important

.sidebar-content
    width: 100% !important
    max-width: 100%
    box-sizing: border-box
    padding: 24px 0 24px 24px
    overflow-x: hidden
    overflow-y: auto

    // На тач-устройствах скроллбар обычно скрыт — добавляем правый отступ
    // независимо от ширины viewport (в т.ч. широкие планшеты).
    @media (hover: none) and (pointer: coarse)
        padding-right: 24px

</style>